import { type ReactNode, useState, useEffect } from 'react'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { HermesChatPanel } from '../hermes/HermesChatPanel'
import { SkerpaAutoContext } from '../hermes/SkerpaAutoContext'
import { SkerpaFloatingBar } from '../hermes/SkerpaFloatingBar'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { useSkerpaStore } from '@/stores/skerpaStore'
import { useSkerpaAutoGreet } from '@/hooks/useSkerpaAutoGreet'

interface Project {
  id: string
  name: string
  slug: string
  role: string
  companyType?: string | null
}

interface MainLayoutProps {
  children: ReactNode
  activeView: string
  onViewChange: (view: string) => void
  currentProject?: Project | null
  onSwitchProject?: () => void
}

const viewTitles: Record<string, string> = {
  workspace: 'Dashboard',
  discovery: 'Discovery Workflow',
  'discovery-assistant': 'AI Discovery Coach',
  interviewer: 'Interview Analysis',
  'ai-interview': 'Interviews',
  'persona-workshop': 'Personas',
  'persona-configurator': 'Persona Details',
  'competitor-map': 'Competitor Map',
  'market-segmentation': 'Market Segments',
  'value-design': 'Value Design',
  'fit-thesis': 'Fit Thesis',
  'canvas-market': 'Positioning',
  'canvas-business': 'Business Model',
  'canvas-solution': 'Problem Statement',
  canvas: 'Strategy Canvas',
  sources: 'Document Analyzer',
  smartdocs: 'Smart Docs',
  docs: 'Documents',
  web: 'Web Sources',
  'requirements': 'Requirements',
  'solution-configurator': 'Solution Configurator',
  'dual-space': 'Problem-Solution Map',
  analysis: 'Validation Analysis',
  dedup: 'Deduplication',
  crm: 'Customers & Contacts',
  knowledge: 'Knowledge Base',
  ontology: 'Ontology Explorer',
  schema: 'Schema Browser',
  graph: 'Graph Explorer',
  tree: 'Data Tree',
  instances: 'Instances',
  query: 'SPARQL Query',
  pdf: 'PDF Viewer',
  reports: 'Reports',
  settings: 'Settings',
  'ai-workflows': 'AI Workflows & Prompts',
  help: 'Help & Documentation',
}

export function MainLayout({
  children,
  activeView,
  onViewChange,
  currentProject,
  onSwitchProject,
}: MainLayoutProps) {
  const title = viewTitles[activeView] || ''
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isSidebarFullscreen, setIsSidebarFullscreen] = useState(false)
  const [isFloatingExpanded, setIsFloatingExpanded] = useState(false)
  const setContextView = useSkerpaContextStore(s => s.setActiveView)
  const openChatTick = useSkerpaContextStore(s => s.openChatTick)
  const skerpaLayout = useSkerpaStore(s => s.skerpaLayout)
  const setSkerpaLayout = useSkerpaStore(s => s.setSkerpaLayout)

  // Sync active view to Skerpa context store
  useEffect(() => {
    setContextView(activeView, title)
  }, [activeView, title, setContextView])

  // openChatTick: open whichever layout is active
  useEffect(() => {
    if (openChatTick === 0) return
    if (skerpaLayout === 'floating') setIsFloatingExpanded(true)
    else setIsSidebarOpen(true)
  }, [openChatTick, skerpaLayout])

  useSkerpaAutoGreet()

  // Header toggle: opens/closes whichever layout is active
  const handleToggleSkerpa = () => {
    if (skerpaLayout === 'floating') {
      setIsFloatingExpanded(v => !v)
    } else {
      setIsSidebarOpen(v => !v)
    }
  }

  // When switching from sidebar → floating, auto-expand the bar
  const handleSwitchToFloating = () => {
    setSkerpaLayout('floating')
    setIsSidebarOpen(false)
    setIsFloatingExpanded(true)
  }

  // When switching from floating bar → sidebar, open sidebar
  const handleSwitchToSidebar = () => {
    setSkerpaLayout('sidebar')
    setIsFloatingExpanded(false)
    setIsSidebarOpen(true)
  }

  const isSkerpaOpen = skerpaLayout === 'floating' ? isFloatingExpanded : isSidebarOpen

  return (
    <div className="h-full w-full flex flex-col overflow-hidden shadow-xl rounded-2xl">
      <SkerpaAutoContext />

      {/* Top bar — full width */}
      <Header
        currentProject={currentProject}
        onSwitchProject={onSwitchProject}
        onToggleHermesChat={handleToggleSkerpa}
        isHermesChatOpen={isSkerpaOpen}
      />

      {/* Content row: Sidebar + Main + Coach */}
      <div className="flex-1 flex overflow-hidden min-h-0 relative">
        <Sidebar
          activeView={activeView}
          onViewChange={onViewChange}
        />

        {/* Main content — hidden when sidebar is fullscreen */}
        {!(isSidebarOpen && isSidebarFullscreen) && (
          <div className="flex-1 flex flex-col overflow-hidden min-w-0 bg-background relative">
            <div className="flex-1 flex flex-col min-h-0 px-6 pt-4 pb-0">
              <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
                {children}
              </main>
            </div>

            {/* Floating bar — only in floating layout mode */}
            {skerpaLayout === 'floating' && (
              <SkerpaFloatingBar
                isExpanded={isFloatingExpanded}
                onExpandedChange={setIsFloatingExpanded}
                onViewChange={onViewChange}
              />
            )}
          </div>
        )}

        {/* Sidebar panel — only in sidebar layout mode */}
        {skerpaLayout === 'sidebar' && (
          <HermesChatPanel
            isOpen={isSidebarOpen}
            isFullscreen={isSidebarFullscreen}
            onClose={() => { setIsSidebarOpen(false); setIsSidebarFullscreen(false) }}
            onViewChange={onViewChange}
            onToggleFullscreen={() => setIsSidebarFullscreen(prev => !prev)}
            onSwitchToFloating={handleSwitchToFloating}
          />
        )}
      </div>
    </div>
  )
}
